/**
 * Wire schemas for the account bindings.
 *
 * The account-security handlers build their bodies in PHP with cast values
 * (`(int)`, `(bool)`, `(string)`), so most fields are validated as they come
 * and not converted. Ids the types declare as `number` go through `wireInt`;
 * ids typed `number | string` stay as sent. Every field a flag adds
 * (`backup_codes`, `method_strength`, `tiered_recovery`, `identity`, ...) is
 * optional, so a flag-off body parses unchanged.
 */
import * as z from 'zod/mini';
import { wireBool, wireInt } from '@dealr/api-common';
import type { WireSchema } from '@dealr/api-common';
import type {
  BackupCodesResponse,
  BusinessSchedule,
  ChallengeResponse,
  GoogleConnection,
  LoginMfaPendingBody,
  LoginSuccessBody,
  MethodCreatedResponse,
  MicrosoftConnection,
  OauthConnection,
  RecoveryRequestResponse,
  RecoveryRequestsResponse,
  ResetVerificationResponse,
  SecurityStatus,
  SessionApprovalsResponse,
  TrustedDevicesResponse,
  SuccessResponse,
  TotpSetupResponse,
  UserDevice,
  UserSettingsRow,
  WebAuthnOptionsResponse,
  WhatsNewSubscriptions,
} from '../types';
import type { IdentityEnrollmentStart, LostDeviceResponse, SamlDetails } from '../accountApi';

const cast = <T>(schema: z.ZodMiniType): WireSchema<T> => schema as unknown as WireSchema<T>;
const obj = z.looseObject;
const opt = z.optional;
const nStr = z.nullable(z.string());
const oNStr = opt(nStr);
const anyId = z.union([z.number(), z.string()]);
const webAuthnJson = z.record(z.string(), z.unknown());

/** A body the binding does not read; declared so the registry sees an explicit contract. */
export const unread = cast<unknown>(z.unknown());

const ackShape = { success: opt(z.boolean()), error: opt(z.string()) };
export const ack = cast<SuccessResponse>(obj(ackShape));

// ── account security ─────────────────────────────────────────────────────

const mfaMethod = obj({
  uuid: z.string(),
  type: z.string(),
  label: oNStr,
  phone_masked: oNStr,
  last_used_at: oNStr,
  created_at: oNStr,
  recommended: opt(z.boolean()),
});

export const securityStatus = cast<SecurityStatus>(
  obj({
    ...ackShape,
    mfa_required: z.boolean(),
    methods: z.array(mfaMethod),
    backup_codes: opt(z.nullable(obj({ remaining: wireInt, generated_at: oNStr, last_used_at: oNStr }))),
    method_strength: opt(z.record(z.string(), z.string())),
    recommend_upgrade: opt(z.boolean()),
    tiered_recovery: opt(z.boolean()),
    recently_recovered: opt(z.boolean()),
    pending_session_approvals: opt(wireInt),
    dealership_admin_count: opt(wireInt),
    identity: opt(z.nullable(obj({}))),
  })
);

export const totpSetup = cast<TotpSetupResponse>(
  obj({ ...ackShape, method_uuid: z.string(), secret: z.string(), otpauth_uri: z.string() })
);
export const webAuthnOptions = cast<WebAuthnOptionsResponse>(obj({ ...ackShape, publicKey: webAuthnJson }));
export const methodCreated = cast<MethodCreatedResponse>(obj({ ...ackShape, method_uuid: z.string() }));

/** A step-up or login challenge: options for a security key, a pending approval for another session. */
export const challenge = cast<ChallengeResponse & { approval_token?: string; pending?: boolean }>(
  obj({
    ...ackShape,
    publicKey: opt(webAuthnJson),
    approval_token: opt(z.string()),
    pending: opt(z.boolean()),
    // Another-device approval: the number to show; the approvers get only the choices.
    match_number: opt(z.nullable(wireInt)),
  })
);
export const stepUpStatus = cast<{ status: string }>(obj({ status: z.string() }));

/** A lost-device answer: a started request, or an ID check (an object, or a status on the reset link). */
export const lostDevice = cast<LostDeviceResponse>(
  obj({
    ...ackShape,
    recovery_request: opt(z.string()),
    identity_check: opt(z.union([z.string(), obj({ status: z.string(), capture_token: oNStr })])),
    capture_token: oNStr,
    requirement: opt(z.string()),
  })
);

export const identityEnrollment = cast<IdentityEnrollmentStart>(
  obj({
    ...ackShape,
    enrollment: obj({ status: z.string(), channel: z.string(), delivered_to: oNStr, capture_token: oNStr }),
  })
);

export const backupCodes = cast<BackupCodesResponse>(obj({ ...ackShape, codes: z.array(z.string()) }));

export const trustedDevices = cast<TrustedDevicesResponse>(
  obj({
    ...ackShape,
    days: wireInt,
    devices: z.array(obj({ uuid: z.string(), label: z.string(), ip_address: oNStr, created_at: oNStr, expires_at: oNStr, last_used_at: oNStr })),
  }),
);

export const sessionApprovals = cast<SessionApprovalsResponse>(
  obj({
    ...ackShape,
    approvals: z.array(
      obj({
        uuid: z.string(),
        ip: oNStr,
        user_agent: oNStr,
        created_at: oNStr,
        expires_at: oNStr,
        kind: opt(z.string()),
        action: opt(z.string()),
        choices: opt(z.array(wireInt)),
      }),
    ),
  })
);

// ── login ────────────────────────────────────────────────────────────────

const loginSuccessShape = {
  token: z.string(),
  type: opt(z.string()),
  mfa_enrollment_required: opt(z.boolean()),
};
export const loginSuccess = cast<LoginSuccessBody>(obj(loginSuccessShape));

export const samlDetails = cast<SamlDetails>(obj({ required: opt(z.boolean()), redirect: opt(z.string()) }));

/** The verify step answers a session, or (202) that another session has not approved yet. */
export const loginVerify = cast<LoginSuccessBody | LoginMfaPendingBody>(
  z.union([obj(loginSuccessShape), obj({ success: z.boolean(), pending: z.boolean() })])
);

// ── tiered recovery ──────────────────────────────────────────────────────

const approval = z.nullable(obj({ user_id: wireInt, at: oNStr, note: oNStr }));

const recoveryRequest = obj({
  uuid: z.string(),
  user_id: wireInt,
  dealer_id: wireInt,
  type: z.string(),
  tier: wireInt,
  status: z.string(),
  new_value: oNStr,
  reason: oNStr,
  initiator_user_id: wireInt,
  initiator_is_dealr: wireBool,
  identity_check: z.string(),
  identity_confirmation: opt(approval),
  approvals: obj({ dealer_admin: approval, callback: approval, supervisor: approval }),
  missing: z.array(z.string()),
  hold_until: oNStr,
  completed_at: oNStr,
  decision_note: oNStr,
  expires_at: oNStr,
  created_at: oNStr,
  name_match: opt(z.nullable(obj({}))),
  target_name: oNStr,
  dealer_name: oNStr,
});

export const recoveryRequestResponse = cast<RecoveryRequestResponse>(obj({ ...ackShape, request: recoveryRequest }));
export const recoveryRequestsResponse = cast<RecoveryRequestsResponse>(
  obj({ ...ackShape, requests: z.array(recoveryRequest) })
);

// ── reset link ───────────────────────────────────────────────────────────

const loginMfaMethod = obj({ uuid: z.string(), type: z.string(), label: oNStr, phone_masked: oNStr });

const resetVerificationShape = {
  ...ackShape,
  requirement: opt(z.string()),
  methods: opt(z.array(loginMfaMethod)),
  method_strength: opt(z.record(z.string(), z.string())),
  identity_check: opt(z.string()),
  activation: opt(z.boolean()),
  invite: opt(z.nullable(obj({ invited_by_name: oNStr, dealership_name: oNStr, brand: oNStr }))),
  capture_token: oNStr,
};
export const resetVerification = cast<ResetVerificationResponse>(obj(resetVerificationShape));
export const resetLostDevice = cast<LostDeviceResponse & ResetVerificationResponse>(
  obj({
    ...resetVerificationShape,
    recovery_request: opt(z.string()),
    identity_check: opt(z.union([z.string(), obj({ status: z.string(), capture_token: oNStr })])),
  })
);
export const resetIdentityCheck = cast<SuccessResponse & { identity_check?: string }>(
  obj({ ...ackShape, identity_check: opt(z.string()) })
);
export const forgotUsername = cast<{ sent?: boolean }>(obj({ ...ackShape, sent: opt(z.boolean()) }));

// ── settings page ────────────────────────────────────────────────────────

/** The user's own row as PDO returns it; the page reads and re-sends it whole. */
export const userSettings = cast<UserSettingsRow>(obj({ id: opt(anyId) }));

const service = obj({ key: z.string(), label: z.string(), available: opt(z.boolean()), connected: opt(z.boolean()), email: oNStr });
const connectionShape = {
  connected: opt(z.boolean()),
  email: oNStr,
  services: opt(z.array(service)),
  interactive_blocked: opt(z.boolean()),
};
export const googleConnection = cast<GoogleConnection>(obj(connectionShape));
export const microsoftConnection = cast<MicrosoftConnection>(obj({ ...connectionShape, available: opt(z.boolean()) }));

export const oauthConnections = cast<OauthConnection[] | null>(
  z.nullable(
    z.array(
      obj({
        id: anyId,
        client_name: z.string(),
        dealer_name: oNStr,
        current_dealer: opt(z.boolean()),
        scope: oNStr,
        issued_at: anyId,
        last_used_at: opt(z.nullable(anyId)),
      })
    )
  )
);

export const userDevices = cast<UserDevice[] | null>(
  z.nullable(
    z.array(
      obj({
        id: anyId,
        platform: z.string(),
        name: oNStr,
        build: opt(z.nullable(wireInt)),
        last_active: wireInt,
        current: opt(z.boolean()),
      })
    )
  )
);

/** Both settings pages read a missing body as no categories. */
export const whatsNewSubscriptions = cast<WhatsNewSubscriptions | null>(
  z.nullable(obj({ unsubscribed: opt(z.array(z.string())), categories: opt(z.record(z.string(), z.string())) }))
);

/** `BusinessHours::getUserScheduleResponseInternal`; every member is read defensively by core-account. */
export const businessSchedule = cast<BusinessSchedule>(
  obj({
    user_id: opt(wireInt),
    dealer_id: opt(wireInt),
    tz: opt(z.nullable(z.string())),
    can_edit: opt(wireBool),
    can_edit_time_off: opt(wireBool),
    can_edit_any: opt(wireBool),
    weekly: opt(z.record(z.string(), obj({}))),
  })
);
